import { z } from 'zod';

/**
 * Verydia Control returns RFC 9457 Problem Details for every non-2xx response.
 *
 * Two fields carry the weight for integrators:
 *
 *  - `code` is the stable, machine-readable discriminator. Clients branch on it.
 *    Status codes are too coarse (403 covers "expired", "suspended" and "revoked",
 *    which need different UI), and `title`/`detail` are prose we reserve the right
 *    to reword.
 *  - `request_id` ties the failure to our logs.
 *
 * Field names are snake_case because RFC 9457 members are, and mixing conventions
 * inside one body is worse than either convention alone.
 */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

/**
 * Problem type URIs are served by the API itself, so `type` genuinely
 * dereferences as RFC 9457 intends.
 *
 * It previously pointed at a docs host that was never provisioned, which meant
 * every error response in production carried a link that failed to resolve.
 * Hosting the catalogue next to the API also means it cannot drift from the
 * codes the API actually emits — both come from `PROBLEM_CATALOGUE` below.
 */
export const PROBLEM_TYPE_BASE = 'https://api.control.parallax-motion.com/errors';

/**
 * The canonical error-code registry. Codes are additive-only: removing or renaming
 * one is a breaking API change even though nothing in the type system says so.
 */
export const ProblemCodes = {
  // 400 / 422
  VALIDATION_FAILED: 'validation_failed',
  MALFORMED_REQUEST: 'malformed_request',
  /**
   * A well-formed request that tried to change something permanent. Distinct
   * from validation_failed: the value is not wrong, the field is closed.
   */
  IMMUTABLE_FIELD: 'immutable_field',
  // 401 / 403
  AUTHENTICATION_REQUIRED: 'authentication_required',
  INVALID_CREDENTIAL: 'invalid_credential',
  INSUFFICIENT_SCOPE: 'insufficient_scope',
  ENVIRONMENT_MISMATCH: 'environment_mismatch',
  // 404 / 409 / 428
  RESOURCE_NOT_FOUND: 'resource_not_found',
  PRECONDITION_REQUIRED: 'precondition_required',
  RESOURCE_CONFLICT: 'resource_conflict',
  IDEMPOTENCY_KEY_REUSED: 'idempotency_key_reused',
  // 402 / 403 — licensing outcomes
  LICENSE_EXPIRED: 'license_expired',
  LICENSE_SUSPENDED: 'license_suspended',
  LICENSE_REVOKED: 'license_revoked',
  SEAT_LIMIT_REACHED: 'seat_limit_reached',
  ACTIVATION_LIMIT_REACHED: 'activation_limit_reached',
  // 503 — transient startup, distinct from a generic outage
  DATABASE_STARTING: 'database_starting',
  // 429 / 5xx
  RATE_LIMITED: 'rate_limited',
  INTERNAL_ERROR: 'internal_error',
  SERVICE_UNAVAILABLE: 'service_unavailable',
} as const;

export type ProblemCode = (typeof ProblemCodes)[keyof typeof ProblemCodes];

/**
 * What each code means, and what a client should do about it.
 *
 * This is the content served at each `type` URI. It is deliberately written for
 * the integrator reading an error at 2am: the `retry` field answers the only
 * question that matters in the moment — is this worth trying again?
 */
export const PROBLEM_CATALOGUE: Readonly<
  Record<ProblemCode, { title: string; meaning: string; retry: 'never' | 'after-change' | 'later' }>
> = Object.freeze({
  validation_failed: {
    title: 'Validation failed',
    meaning:
      'The request body or query string did not match the schema. See `errors` for the offending fields.',
    retry: 'after-change',
  },
  malformed_request: {
    title: 'Malformed request',
    meaning:
      'The request could not be parsed at all — usually invalid JSON or a wrong content type.',
    retry: 'after-change',
  },
  immutable_field: {
    title: 'This value is permanent',
    meaning:
      'The request tried to change a field that is fixed once set — a product slug, for ' +
      'example. The value is not invalid; the field is closed. Retrying without it succeeds.',
    retry: 'after-change',
  },
  authentication_required: {
    title: 'Authentication required',
    meaning: 'No bearer token was presented. Obtain one through the console sign-in flow.',
    retry: 'after-change',
  },
  invalid_credential: {
    title: 'Invalid credential',
    meaning:
      'The token was rejected: expired, malformed, or issued by a different user pool. Acquire a fresh one.',
    retry: 'after-change',
  },
  insufficient_scope: {
    title: 'Insufficient permissions',
    meaning:
      'The caller is authenticated but not permitted to perform this action. The specific policy reason is deliberately not disclosed.',
    retry: 'never',
  },
  environment_mismatch: {
    title: 'Environment mismatch',
    meaning: 'The credential belongs to a different environment than the resource it addressed.',
    retry: 'never',
  },
  resource_not_found: {
    title: 'Resource not found',
    meaning:
      'No such resource, or it is not visible to this caller. The two cases are intentionally indistinguishable, so that probing for ids reveals nothing.',
    retry: 'never',
  },
  precondition_required: {
    title: 'Precondition required',
    meaning:
      'This edit must state the version it is based on. Read the resource, send its `version` back, and the server will refuse the write if somebody changed it in between.',
    retry: 'after-change',
  },
  resource_conflict: {
    title: 'Conflict',
    meaning:
      'The request contradicts the current state — a duplicate slug, or an invariant such as the last remaining owner.',
    retry: 'after-change',
  },
  idempotency_key_reused: {
    title: 'Idempotency key reused',
    meaning:
      'This key was already used with a different request body. Use a new key, or replay the identical request.',
    retry: 'after-change',
  },
  license_expired: {
    title: 'License expired',
    meaning: 'The license term has ended. Renewal is a commercial action, not a retryable one.',
    retry: 'never',
  },
  license_suspended: {
    title: 'License suspended',
    meaning: 'The license is temporarily inactive. It may be restored by the vendor.',
    retry: 'never',
  },
  license_revoked: {
    title: 'License revoked',
    meaning: 'The license was permanently withdrawn and will not be restored.',
    retry: 'never',
  },
  seat_limit_reached: {
    title: 'Seat limit reached',
    meaning: 'Every seat on this license is assigned. Release a seat or raise the limit.',
    retry: 'never',
  },
  activation_limit_reached: {
    title: 'Activation limit reached',
    meaning: 'This license has been activated on the maximum number of devices.',
    retry: 'never',
  },
  database_starting: {
    title: 'Verydia is starting up',
    meaning:
      'The control plane was idle and its database is waking. This resolves on its own in a few seconds — honour `Retry-After` and try again.',
    retry: 'later',
  },
  rate_limited: {
    title: 'Rate limited',
    meaning: 'Too many requests. Back off and retry — respect `Retry-After` when present.',
    retry: 'later',
  },
  internal_error: {
    title: 'Internal error',
    meaning:
      'Something failed on our side. The detail is withheld deliberately; quote `request_id` and we can find the exact failure in our logs.',
    retry: 'later',
  },
  service_unavailable: {
    title: 'Service unavailable',
    meaning: 'A dependency is temporarily unavailable. Retry with backoff.',
    retry: 'later',
  },
});

export const problemSchema = z
  .object({
    type: z.url().describe('Dereferenceable URI identifying the problem type.'),
    title: z.string().describe('Short, human-readable summary. Stable per problem type.'),
    status: z.int().min(400).max(599).describe('HTTP status code, repeated in the body.'),
    code: z
      .string()
      .describe('Stable machine-readable error code. Branch on this, not on the status.'),
    detail: z
      .string()
      .describe('Human-readable explanation of this specific occurrence. Never a stack trace.'),
    request_id: z
      .string()
      .describe('Identifier of the request that failed. Quote this in support tickets.'),
    errors: z
      .array(
        z.object({
          path: z.string().describe('JSON Pointer-ish path to the offending field.'),
          message: z.string(),
        }),
      )
      .optional()
      .describe('Present on validation failures.'),
  })
  .meta({
    id: 'Problem',
    title: 'Problem',
    description:
      'RFC 9457 Problem Details. Branch on `code`; quote `request_id` when reporting a failure.',
  });

export type Problem = z.infer<typeof problemSchema>;

export interface ProblemInit {
  status: number;
  code: ProblemCode | string;
  title: string;
  detail: string;
  requestId: string;
  errors?: ReadonlyArray<{ path: string; message: string }>;
}

export function buildProblem(init: ProblemInit): Problem {
  const problem: Problem = {
    type: `${PROBLEM_TYPE_BASE}/${init.code}`,
    title: init.title,
    status: init.status,
    code: init.code,
    detail: init.detail,
    request_id: init.requestId,
  };
  return init.errors && init.errors.length > 0
    ? { ...problem, errors: init.errors.map((e) => ({ ...e })) }
    : problem;
}
